"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const text = "Details of Our Services";

export default function ServiceCards() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  return (
    <div className="mt-30 mb-20 space-y-12">
      {/* Animated Hero Section */}
      <div className="relative w-full overflow-hidden py-24 md:py-32" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}>
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <svg
            className="h-full w-full"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 L100,0 L100,100 L0,100 Z"
              fill="none"
              stroke={colors.white}
              strokeWidth="0.5"
              strokeDasharray="2 2"
            />
          </svg>
        </div>

        {/* Content container */}
        <div className="container relative mx-auto px-6">
          <h1 className="text-center font-orbitron text-4xl font-bold text-white sm:text-5xl md:text-6xl lg:text-7xl">
            <span className="relative inline-block">
              {text.substring(0, currentIndex)}
              <motion.span
                className="absolute -right-3 top-0 h-12 w-1 bg-white"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scaleY: [1, 1.2, 1],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 0.8,
                  ease: "easeInOut",
                }}
              />
            </span>
          </h1>

          {/* Decorative elements */}
          <div className="mt-12 flex justify-center space-x-4">
            {[1, 2, 3].map((item) => (
              <motion.div
                key={item}
                className="h-2 rounded-full bg-white/30"
                initial={{ width: 0 }}
                animate={{ width: item % 2 === 0 ? "40px" : "20px" }}
                transition={{
                  duration: 0.6,
                  delay: item * 0.2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              />
            ))}
          </div>
        </div>

        {/* Wave divider at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-12 overflow-hidden">
          <svg
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="h-full w-full"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
              fill={colors.white}
            ></path>
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
              fill={colors.white}
            ></path>
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              fill={colors.white}
            ></path>
          </svg>
        </div>
      </div>

      {/* Training & Consultancy Section */}
      <div className="mt-20 px-4 lg:mx-auto lg:max-w-7xl" id="training-consultancy">
        <div className="py-5">
          {/* Header Section */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-16 text-center text-4xl font-bold sm:text-5xl"
            style={{ color: colors.primary }}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
                Training & Consultancy
              </span>
              <motion.span
                className="absolute bottom-0 left-1/2 h-1 w-24 -translate-x-1/2 transform bg-gradient-to-r"
                style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          {/* Value Proposition */}
          <div className="mb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6" style={{ color: colors.primary }}>Building Capacity Through Knowledge</h3>
              <p className="text-lg mb-8" style={{ color: colors.textDark }}>
                Our training and consultancy programs are designed to empower individuals and organizations with 
                practical skills and strategic insights. We bridge the gap between theory and practice through 
                customized solutions tailored to Ethiopia's unique business landscape.
              </p>
              
              <div className="space-y-6">
                {[
                  "Expert-led practical training sessions",
                  "Customized consultancy for business growth",
                  "Certification programs in key sectors",
                  "Ongoing support and mentorship"
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="mr-4 mt-1 h-5 w-5 rounded-full" style={{ backgroundColor: colors.primaryLight }}></div>
                    <span style={{ color: colors.textDark }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative aspect-video rounded-xl overflow-hidden shadow-lg">
              <Image
                src="/images/about/training34.png"
                alt="Training session"
                fill
                className="object-cover"
              />
            </div>
          </div>

          {/* Training Programs */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold mb-12 text-center" style={{ color: colors.primary }}>Our Training Programs</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Entrepreneurship Development",
                  focus: "Startup founders and small business owners",
                  duration: "2-4 weeks",
                  icon: "💼"
                },
                {
                  title: "Financial Management",
                  focus: "Finance professionals and business managers",
                  duration: "3-5 days",
                  icon: "📊"
                },
                {
                  title: "Agricultural Best Practices",
                  focus: "Farmers and agribusinesses",
                  duration: "1-2 weeks",
                  icon: "🌱"
                },
                {
                  title: "Digital Skills",
                  focus: "All professionals and students",
                  duration: "1-3 days",
                  icon: "💻"
                },
                {
                  title: "Leadership & Management",
                  focus: "Mid-level to senior managers",
                  duration: "5 days",
                  icon: "👔"
                },
                {
                  title: "Sector-Specific Technical Training",
                  focus: "Industry professionals",
                  duration: "Custom",
                  icon: "🛠️"
                }
              ].map((program, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="border rounded-lg p-6 hover:shadow-md transition-shadow"
                  style={{ borderColor: `${colors.primary}20` }}
                >
                  <div className="text-4xl mb-4">{program.icon}</div>
                  <h4 className="text-xl font-bold mb-2" style={{ color: colors.primary }}>{program.title}</h4>
                  <p className="mb-3" style={{ color: colors.textDark }}><span className="font-medium">Focus:</span> {program.focus}</p>
                  <p style={{ color: colors.textDark }}><span className="font-medium">Duration:</span> {program.duration}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Consultancy Services */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold mb-12 text-center" style={{ color: colors.primary }}>Consultancy Services</h3>
            
            <div className="space-y-12">
              {[
                {
                  title: "Business Strategy Development",
                  description: "Comprehensive analysis and roadmap creation for business growth and sustainability",
                  deliverables: ["Market analysis", "SWOT analysis", "5-year strategic plan"]
                },
                {
                  title: "Financial Systems Review",
                  description: "Evaluation and improvement of financial management systems and processes",
                  deliverables: ["Gap analysis", "Process mapping", "Implementation plan"]
                },
                {
                  title: "Organizational Development",
                  description: "Enhancing organizational structure and human resource management",
                  deliverables: ["Structure assessment", "Competency framework", "Change management plan"]
                }
              ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="p-8 rounded-lg shadow-sm border"
                  style={{ backgroundColor: colors.white, borderColor: `${colors.primary}10` }}
                >
                  <h4 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>{service.title}</h4>
                  <p className="mb-6" style={{ color: colors.textDark }}>{service.description}</p>
                  
                  <h5 className="font-semibold mb-3" style={{ color: colors.primary }}>Key Deliverables:</h5>
                  <ul className="space-y-2">
                    {service.deliverables.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <div className="mr-3 mt-1 h-2 w-2 rounded-full" style={{ backgroundColor: colors.primaryLight }}></div>
                        <span style={{ color: colors.textDark }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Process Section */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold mb-12 text-center" style={{ color: colors.primary }}>How It Works</h3>
            
            <div className="relative">
              <div className="absolute left-1/2 top-12 hidden h-[calc(100%-6rem)] w-0.5 -translate-x-1/2 transform md:block" style={{ backgroundColor: `${colors.primary}20` }}></div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  {
                    step: "1",
                    title: "Needs Assessment",
                    description: "We evaluate your specific requirements"
                  },
                  {
                    step: "2",
                    title: "Program Design",
                    description: "Customized solution development"
                  },
                  {
                    step: "3",
                    title: "Delivery",
                    description: "Implementation of training or consultancy"
                  },
                  {
                    step: "4",
                    title: "Follow-up",
                    description: "Evaluation and ongoing support"
                  }
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative flex flex-col items-center text-center"
                  >
                    <div className="relative z-10 mb-4 flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})` }}>
                      {step.step}
                    </div>
                    <h4 className="mb-2 text-xl font-bold" style={{ color: colors.primary }}>
                      {step.title}
                    </h4>
                    <p style={{ color: colors.textDark }}>{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-block rounded-xl p-1"
              style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
            >
              <div className="rounded-lg px-8 py-6" style={{ backgroundColor: colors.white }}>
                <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>Ready to Enhance Your Capabilities?</h3>
                <p className="mb-6 max-w-2xl mx-auto" style={{ color: colors.textDark }}>
                  Our training and consultancy services are tailored to help you achieve your professional and organizational goals.
                </p>
                <Link
                  href="/contact"
                  className="inline-flex items-center rounded-lg px-8 py-3 font-medium text-white shadow-md hover:shadow-lg transition-colors"
                  style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9' }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
                >
                  Contact Our Team
                  <svg
                    className="ml-2 h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}